import React from 'react';
import { motion } from 'framer-motion';
import { cvData } from '../data/cvData';
import { uiTranslations } from '../data/translations';
import { useLanguage } from '../context/LanguageContext';
import SectionHeader from './SectionHeader';
import SafeIcon from '../common/SafeIcon';

export default function Experience() {
  const { language } = useLanguage();
  const data = cvData[language];
  const t = uiTranslations[language].sections.experience;
  const labels = uiTranslations[language].experience;

  if (!data.experience || data.experience.length === 0) return null;

  return (
    <section id="experience" className="py-24 bg-slate-50/50">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <SectionHeader title={t.title} label={t.label} />
        <div className="space-y-16">
          {data.experience.map((exp, index) => (
            <motion.div key={exp.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: index * 0.1 }} className="relative group bg-white rounded-3xl border border-slate-200 shadow-sm hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-500 overflow-hidden">
              <div className="h-1.5 w-full bg-gradient-to-r from-primary via-accent to-primary/80" />
              <div className="p-8 md:p-10 border-b border-slate-100">
                <div className="flex flex-col gap-6">
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="space-y-2">
                      <h3 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight leading-none">
                        {exp.role}
                      </h3>
                      <div className="flex flex-col gap-1">
                        <p className="text-xl font-bold text-accent">{exp.company}</p>
                        {exp.exitReason && (
                          <div className="flex items-center gap-2 text-slate-400 mt-1">
                            <SafeIcon name="Info" className="w-3.5 h-3.5" />
                            <span className="text-sm font-medium leading-none">
                              {exp.exitReason}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-full border border-slate-200 shadow-sm self-start md:mt-1">
                      <SafeIcon name="Calendar" className="w-4 h-4 text-slate-400" />
                      <span className="text-sm font-bold text-slate-600 whitespace-nowrap">{exp.period}</span>
                    </div>
                  </div>
                  {exp.product && (
                    <div className="bg-slate-900 rounded-2xl p-5 flex items-start gap-4 shadow-lg shadow-slate-900/10">
                      <div className="w-10 h-10 rounded-xl bg-accent/20 flex items-center justify-center text-accent flex-shrink-0">
                        <SafeIcon name="Box" className="w-5 h-5" />
                      </div>
                      <div>
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] block mb-1">
                          {language === 'de' ? 'Produkt & Fokus' : 'Product & Focus'}
                        </span>
                        <p className="text-white text-lg font-medium leading-snug">
                          {exp.product}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-slate-100">
                <div className="p-8 md:p-10 bg-white">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500">
                      <SafeIcon name="Target" className="w-4 h-4" />
                    </div>
                    <h4 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em]">
                      {labels.responsibilities}
                    </h4>
                  </div>
                  <ul className="space-y-4">
                    {exp.responsibilities.map((item, i) => (
                      <li key={i} className="flex items-start gap-4 group/item">
                        <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-slate-300 group-hover/item:bg-accent transition-colors flex-shrink-0" />
                        <p className="text-slate-600 text-[15px] leading-relaxed font-medium">
                          {item}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="p-8 md:p-10 bg-slate-50/30">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center text-accent">
                      <SafeIcon name="TrendingUp" className="w-4 h-4" />
                    </div>
                    <h4 className="text-xs font-black text-accent uppercase tracking-[0.2em]">
                      {labels.results}
                    </h4>
                  </div>
                  <ul className="space-y-4">
                    {exp.results.map((item, i) => (
                      <li key={i} className="flex items-start gap-4 bg-white p-4 rounded-xl border border-slate-100 shadow-sm group/result hover:border-accent/20 transition-all">
                        <div className="mt-1 w-5 h-5 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0">
                          <SafeIcon name="Check" className="w-3 h-3 text-green-500" />
                        </div>
                        <p className="text-slate-600 text-[15px] leading-relaxed">
                          {item}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="px-10 py-5 bg-white border-t border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
                {exp.techStack && (
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest mr-2">Tech Stack:</span>
                    {exp.techStack.map((tech, i) => (
                      <span key={i} className="text-[11px] font-bold text-slate-500 bg-slate-50 px-3 py-1 rounded-md border border-slate-100">
                        {tech}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}